import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import useSettings from "../../data/useSettings";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { ISetting, SettingInputType, SettingType } from "../../models/Setting";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import {
  CURRENCY_SETTING_KEY,
  DEFAULT_CURRENCY,
  formatPrice,
  isCurrencyCode,
  setCurrencyCode,
} from "../../utils/currency";

const COMMON_CODES = ["GBP", "EUR", "PLN", "USD"];

// The one place the business's trading currency is set. The same "Currency" setting is read by
// the API for invoice PDFs, lead documents and the Xero/Oracle exports, so screens and documents
// always agree.
export default function BusinessCurrencyCard() {
  const { t } = useTranslation("common");
  const { settings, create, update } = useSettings();
  const { saveHandlers } = useDefaultCRUDHandlers(t("Currency"));

  const setting = settings.find((s) => s.key === CURRENCY_SETTING_KEY);
  const current = isCurrencyCode(setting?.value) ? setting!.value.trim().toUpperCase() : DEFAULT_CURRENCY;
  const [value, setValue] = useState(current);

  useEffect(() => {
    setValue(current);
  }, [current]);

  const code = value.trim().toUpperCase();
  const valid = isCurrencyCode(code);
  const changed = code !== current;
  const saving = create.isLoading || update.isLoading;

  let preview = "";
  if (valid) {
    try {
      preview = formatPrice(1234.5, { currency: code });
    } catch {
      preview = "";
    }
  }

  const save = async () => {
    if (!valid || !changed) return;
    if (setting?.id) {
      await update.mutateAsync({ ...setting, value: code } as ISetting, saveHandlers);
    } else {
      await create.mutateAsync(
        {
          key: CURRENCY_SETTING_KEY,
          value: code,
          settingType: SettingType.General,
          inputType: SettingInputType.Text,
          required: false,
        } as ISetting,
        saveHandlers
      );
    }
    setCurrencyCode(code);
  };

  return (
    <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-900">{t("Business currency")}</h3>
      <p className="mt-1 text-sm text-gray-500">
        {t("The ISO code used for every price on screen and on invoices, documents and exports. Other pages pick it up on their next load.")}
      </p>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="business-currency" className="text-sm font-medium text-gray-500">
            {t("Currency")}
          </label>
          <input
            id="business-currency"
            list="business-currency-codes"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            maxLength={3}
            placeholder={DEFAULT_CURRENCY}
            className="mt-1 block w-28 rounded-md border border-gray-300 px-3 py-2 text-sm uppercase shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <datalist id="business-currency-codes">
            {COMMON_CODES.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </div>
        <ButtonInput
          label={t("Save")}
          isSubmit={false}
          onClick={save}
          disabled={!valid || !changed || saving}
          isLoading={saving}
          classes=""
        />
        {valid ? (
          <span className="pb-2 text-sm text-gray-500">
            {t("Preview")}: <span className="font-medium text-gray-900">{preview}</span>
          </span>
        ) : (
          <span className="pb-2 text-sm text-red-600">
            {t("Enter a three-letter ISO currency code, e.g. GBP, EUR or PLN")}
          </span>
        )}
      </div>
    </div>
  );
}
